/**
 * AllInboxesSnapshot · v2.7.45+E7 (2026-02-22)
 * ═════════════════════════════════════════════════════════════════════
 * Spec §1.2 — aggregated multi-mailbox dashboard: Total Unread, Flagged,
 * Drafts, Sent Today, Important, Spam, Trash, Recent Emails + Mailbox
 * Activity & Sync Status rows.
 *
 * Talks to the new `GET /api/admin/email/inbox-snapshot` endpoint which
 * is server-side cached for 60s. A manual refresh button bypasses the
 * cache via `?force=1`.
 */
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import adminApi from "@/lib/adminApi";
import {
  RefreshCw, Loader2, Mail, Flag, FileText, Send, Star, ShieldAlert,
  Trash2, Inbox, CheckCircle2, AlertCircle,
} from "lucide-react";

const TILES = [
  { key: "unread",     label: "Unread",      Icon: Mail,        accent: "sky" },
  { key: "flagged",    label: "Flagged",     Icon: Flag,        accent: "amber" },
  { key: "drafts",     label: "Drafts",      Icon: FileText,    accent: "slate" },
  { key: "sent_today", label: "Sent Today",  Icon: Send,        accent: "emerald" },
  { key: "important",  label: "Important",   Icon: Star,        accent: "rose" },
  { key: "spam",       label: "Spam",        Icon: ShieldAlert, accent: "orange" },
  { key: "trash",      label: "Trash",       Icon: Trash2,      accent: "zinc" },
  { key: "recent",     label: "Recent",      Icon: Inbox,       accent: "violet" },
];

const ACCENT_TEXT = {
  sky:     "text-sky-600 dark:text-sky-300",
  amber:   "text-amber-600 dark:text-amber-300",
  slate:   "text-slate-600 dark:text-slate-300",
  emerald: "text-emerald-600 dark:text-emerald-300",
  rose:    "text-rose-600 dark:text-rose-300",
  orange:  "text-orange-600 dark:text-orange-300",
  zinc:    "text-zinc-600 dark:text-zinc-300",
  violet:  "text-violet-600 dark:text-violet-300",
};

export default function AllInboxesSnapshot() {
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [forcing, setForcing] = useState(false);

  const load = useCallback(async (force = false) => {
    force ? setForcing(true) : setBusy(true);
    try {
      const { data } = await adminApi.get(`/admin/email/inbox-snapshot${force ? "?force=1" : ""}`);
      setSnapshot(data);
    } catch {
      // Silent — backend cache may still serve us a stale snapshot.
    } finally {
      setBusy(false);
      setForcing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totals = snapshot?.totals || {};
  const perMb = snapshot?.per_mailbox || [];

  return (
    <section
      className="rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 overflow-hidden"
      data-testid="all-inboxes-snapshot"
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-zinc-800 bg-slate-50/50 dark:bg-zinc-900/40">
        <div className="flex items-center gap-2">
          <Inbox className="h-4 w-4 text-violet-500"/>
          <div className="text-sm font-extrabold uppercase tracking-widest text-slate-900 dark:text-zinc-100">
            All Inboxes Snapshot
          </div>
          {snapshot?.cached ? (
            <Badge className="bg-violet-500/10 text-violet-600 dark:text-violet-300 border-violet-400/30 text-[10px]">
              Cached · {snapshot.cache_age_s}s
            </Badge>
          ) : (
            <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 border-emerald-400/30 text-[10px]">
              Fresh
            </Badge>
          )}
        </div>
        <Button
          variant="ghost" size="sm" className="h-7 px-2 text-[11px] gap-1.5"
          onClick={() => load(true)}
          disabled={forcing || busy}
          data-testid="snapshot-refresh"
        >
          {forcing ? <Loader2 className="h-3 w-3 animate-spin"/> : <RefreshCw className="h-3 w-3"/>}
          Refresh
        </Button>
      </header>

      {/* Tiles grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-px bg-slate-200 dark:bg-zinc-800">
        {TILES.map((t) => {
          const Icon = t.Icon;
          const value = totals[t.key] ?? 0;
          return (
            <div
              key={t.key}
              className="bg-white dark:bg-zinc-950 px-4 py-3 flex flex-col items-start gap-1"
              data-testid={`snapshot-tile-${t.key}`}
            >
              <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold ${ACCENT_TEXT[t.accent]}`}>
                <Icon className="h-3 w-3"/>{t.label}
              </div>
              <div className="text-2xl font-extrabold text-slate-900 dark:text-zinc-100 tabular-nums">
                {busy && !snapshot ? <Loader2 className="h-5 w-5 animate-spin text-slate-400"/> : value}
              </div>
            </div>
          );
        })}
      </div>

      {/* Activity / sync row */}
      {perMb.length > 0 && (
        <div className="border-t border-slate-200 dark:border-zinc-800 px-4 py-3 bg-slate-50/30 dark:bg-zinc-900/20">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400 mb-2">
            Mailbox Activity &amp; Sync
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-[11px]">
            {perMb.map((m) => (
              <div key={m.id} className="flex items-center gap-1.5">
                {m.metrics?.ok ? (
                  <CheckCircle2 className="h-3 w-3 text-emerald-500"/>
                ) : (
                  <AlertCircle className="h-3 w-3 text-rose-500" title={m.metrics?.error}/>
                )}
                <span className="font-mono text-slate-700 dark:text-zinc-300">{m.email}</span>
                <Badge className="bg-sky-500/10 text-sky-600 dark:text-sky-300 border-sky-400/20 text-[9px] px-1.5">
                  {m.metrics?.unread ?? 0} new
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent activity timestamp */}
      {snapshot?.generated_at && (
        <div className="border-t border-slate-200 dark:border-zinc-800 px-4 py-2 text-[10px] text-slate-500 dark:text-zinc-500">
          Snapshot generated {new Date(snapshot.generated_at).toLocaleString()}
        </div>
      )}
    </section>
  );
}
